import React, { useEffect, useState } from 'react';
import {
  Card, Button, Table, Tag, Space, Typography, Spin, Alert, Modal,
  Form, Input, Select, Progress, message, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, CheckCircleOutlined,
  LockOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { complianceApi } from '@/api/complianceApi';
import type { ComplianceMatrixData, ComplianceRequirementData, ComplianceStatus } from '@/types/compliance';
import { useAuth } from '@/auth/useAuth';

const { Text } = Typography;
const { Option } = Select;

const STATUS_CONFIG: Record<ComplianceStatus, { label: string; color: string }> = {
  FULL:          { label: 'Fully Compliant',    color: 'success' },
  PARTIAL:       { label: 'Partial',             color: 'warning' },
  NON_COMPLIANT: { label: 'Non-Compliant',       color: 'error' },
  NA:            { label: 'N/A',                 color: 'default' },
  PENDING:       { label: 'Pending',             color: 'processing' },
};

const CATEGORIES = ['Technical', 'Management', 'Past Performance', 'Price/Cost', 'Small Business', 'Other'];

interface ComplianceMatrixFormProps {
  opportunityId: string;
  onComplete: () => void;
}

const ComplianceMatrixForm: React.FC<ComplianceMatrixFormProps> = ({ opportunityId, onComplete }) => {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(['TECHNICAL_WRITER', 'ADMIN', 'CEO', 'BD_MANAGER', 'CLOUD_ARCHITECT', 'QC_LEAD']);

  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState<ComplianceMatrixData | null>(null);
  const [requirements, setRequirements] = useState<ComplianceRequirementData[]>([]);
  const [initializing, setInitializing] = useState(false);

  // Add/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReq, setEditingReq] = useState<ComplianceRequirementData | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const mat = await complianceApi.getMatrix(opportunityId);
      setMatrix(mat);
      if (mat) {
        const reqs = await complianceApi.getRequirements(opportunityId, mat.id);
        setRequirements(reqs);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [opportunityId]);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const mat = await complianceApi.createMatrix(opportunityId);
      setMatrix(mat);
      setRequirements([]);
      message.success('Compliance matrix initialized');
      onComplete();
    } catch {
      message.error('Failed to initialize compliance matrix');
    } finally {
      setInitializing(false);
    }
  };

  const openAddModal = () => {
    setEditingReq(null);
    form.resetFields();
    form.setFieldsValue({ complianceStatus: 'PENDING', sortOrder: requirements.length + 1 });
    setModalOpen(true);
  };

  const openEditModal = (req: ComplianceRequirementData) => {
    setEditingReq(req);
    form.setFieldsValue({
      requirementRef: req.requirementRef,
      requirementText: req.requirementText,
      category: req.category,
      complianceStatus: req.complianceStatus,
      responseText: req.responseText,
      evidenceNotes: req.evidenceNotes,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!matrix) return;
      setSaving(true);
      if (editingReq) {
        const updated = await complianceApi.updateRequirement(
          opportunityId, matrix.id, editingReq.id, values,
        );
        setRequirements((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        // Refresh matrix stats
        const mat = await complianceApi.getMatrix(opportunityId);
        if (mat) setMatrix(mat);
        message.success('Requirement updated');
      } else {
        const created = await complianceApi.addRequirement(opportunityId, matrix.id, values);
        setRequirements((prev) => [...prev, created]);
        const mat = await complianceApi.getMatrix(opportunityId);
        if (mat) setMatrix(mat);
        message.success('Requirement added');
      }
      setModalOpen(false);
      onComplete();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
      message.error('Failed to save requirement');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (req: ComplianceRequirementData, newStatus: ComplianceStatus) => {
    if (!matrix) return;
    try {
      const updated = await complianceApi.updateRequirement(
        opportunityId, matrix.id, req.id, { complianceStatus: newStatus },
      );
      setRequirements((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      const mat = await complianceApi.getMatrix(opportunityId);
      if (mat) setMatrix(mat);
      onComplete();
    } catch {
      message.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading compliance matrix...
      </Card>
    );
  }

  const compliantPct = matrix && matrix.totalRequirements > 0
    ? Math.round(((matrix.compliantCount + matrix.naCount) / matrix.totalRequirements) * 100)
    : 0;

  const columns = [
    {
      title: 'Ref',
      dataIndex: 'requirementRef',
      key: 'ref',
      width: 80,
      render: (v: string) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: 'Requirement',
      dataIndex: 'requirementText',
      key: 'text',
      render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (v: string) => v ? <Tag style={{ fontSize: 11 }}>{v}</Tag> : '—',
    },
    {
      title: 'Status',
      dataIndex: 'complianceStatus',
      key: 'status',
      width: 160,
      render: (status: ComplianceStatus, record: ComplianceRequirementData) =>
        canEdit ? (
          <Select
            size="small"
            value={status}
            style={{ width: 150 }}
            onChange={(val) => handleStatusChange(record, val as ComplianceStatus)}
          >
            {(Object.keys(STATUS_CONFIG) as ComplianceStatus[]).map((s) => (
              <Option key={s} value={s}>
                <Tag color={STATUS_CONFIG[s].color} style={{ fontSize: 11, margin: 0 }}>
                  {STATUS_CONFIG[s].label}
                </Tag>
              </Option>
            ))}
          </Select>
        ) : (
          <Tag color={STATUS_CONFIG[status]?.color || 'default'} style={{ fontSize: 11 }}>
            {STATUS_CONFIG[status]?.label || status}
          </Tag>
        ),
    },
    {
      title: 'Response / Notes',
      dataIndex: 'responseText',
      key: 'response',
      render: (v: string) => v
        ? <Tooltip title={v}><Text style={{ fontSize: 12 }} ellipsis>{v}</Text></Tooltip>
        : <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>No response yet</Text>,
    },
    ...(canEdit ? [{
      title: '',
      key: 'actions',
      width: 60,
      render: (_: unknown, record: ComplianceRequirementData) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => openEditModal(record)}
        />
      ),
    }] : []),
  ];

  return (
    <>
      <Card
        size="small"
        style={{ marginTop: 12, borderColor: matrix && matrix.totalRequirements > 0 ? 'var(--color-success)' : 'var(--color-warning)' }}
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: 'var(--color-primary)' }} />
            <Text strong style={{ fontSize: 14 }}>Phase D: Compliance Matrix</Text>
            {matrix && matrix.totalRequirements > 0 && (
              <Tag icon={<CheckCircleOutlined />} color="success">
                {matrix.totalRequirements} requirement{matrix.totalRequirements !== 1 ? 's' : ''}
              </Tag>
            )}
          </Space>
        }
        extra={
          canEdit && matrix && (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={openAddModal}
            >
              Add Requirement
            </Button>
          )
        }
      >
        {!canEdit && (
          <Alert
            message={<span><LockOutlined /> Read-only — only the BD Manager, Cloud Architect, QC Lead, Technical Writer, Admin, or CEO can edit this section.</span>}
            type="info"
            showIcon={false}
            style={{ marginBottom: 12 }}
          />
        )}

        {!matrix ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Alert
              message="Compliance matrix not yet initialized"
              description="Initialize the compliance matrix to begin tracking RFP requirements for this opportunity."
              type="warning"
              showIcon
              style={{ marginBottom: 16, textAlign: 'left' }}
            />
            {canEdit && (
              <Button
                type="primary"
                icon={<SafetyCertificateOutlined />}
                loading={initializing}
                onClick={handleInitialize}
              >
                Initialize Compliance Matrix
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Stats bar */}
            {matrix.totalRequirements > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
                  <Text style={{ fontSize: 12 }}>
                    Total: <strong>{matrix.totalRequirements}</strong>
                  </Text>
                  <Text style={{ fontSize: 12, color: 'var(--color-success)' }}>
                    Compliant: <strong>{matrix.compliantCount}</strong>
                  </Text>
                  <Text style={{ fontSize: 12, color: 'var(--color-warning)' }}>
                    Partial: <strong>{matrix.partialCount}</strong>
                  </Text>
                  <Text style={{ fontSize: 12, color: 'var(--color-error)' }}>
                    Non-Compliant: <strong>{matrix.nonCompliantCount}</strong>
                  </Text>
                  <Text style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                    N/A: <strong>{matrix.naCount}</strong>
                  </Text>
                </div>
                <Progress
                  percent={compliantPct}
                  size="small"
                  strokeColor="var(--color-success)"
                  format={(pct) => `${pct}% addressed`}
                />
              </div>
            )}

            {requirements.length === 0 ? (
              <Alert
                message="No requirements added yet"
                description={canEdit ? 'Use the "Add Requirement" button to enter RFP compliance requirements one by one.' : 'No compliance requirements have been entered yet.'}
                type="info"
                showIcon
              />
            ) : (
              <Table
                dataSource={requirements}
                columns={columns}
                rowKey="id"
                size="small"
                pagination={requirements.length > 10 ? { pageSize: 10, size: 'small' } : false}
              />
            )}
          </>
        )}
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        title={editingReq ? 'Edit Requirement' : 'Add Compliance Requirement'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editingReq ? 'Update' : 'Add'}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item name="requirementRef" label="Requirement Ref" style={{ width: 140 }}>
              <Input placeholder="e.g. L-001" />
            </Form.Item>
            <Form.Item name="category" label="Category" style={{ width: 200 }}>
              <Select placeholder="Select category" allowClear>
                {CATEGORIES.map((c) => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="complianceStatus" label="Status" style={{ width: 180 }}>
              <Select>
                {(Object.keys(STATUS_CONFIG) as ComplianceStatus[]).map((s) => (
                  <Option key={s} value={s}>{STATUS_CONFIG[s].label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          <Form.Item
            name="requirementText"
            label="Requirement Text"
            rules={[{ required: true, message: 'Requirement text is required' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter the full requirement from the RFP / solicitation document..."
            />
          </Form.Item>

          <Form.Item name="responseText" label="Response / Approach">
            <Input.TextArea
              rows={2}
              placeholder="How will this requirement be addressed in the proposal?"
            />
          </Form.Item>

          <Form.Item name="evidenceNotes" label="Evidence / Notes">
            <Input.TextArea
              rows={2}
              placeholder="Reference documents, page numbers, section citations..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ComplianceMatrixForm;
